import { type CellAgreement, type CheckedReason } from '../../services/bulkEnrichFields';
import { descriptionRewritePreTicks, type DescriptionSource } from './descriptionSource';

/**
 * The three decisions the reference editor's AI review table makes about a
 * calibre-tier suggestion.
 *
 * They live here rather than inline in ReferenceEditor because each is a rule
 * with edge cases worth pinning — and because the component around them is a
 * ~10k-line file with no test harness. The RESOLUTION of a tier suggestion
 * (which tier does "Elaboré" mean?) is not here: that is
 * `resolveTierSuggestion` in calibreTiers.ts, which these compose with.
 */

/**
 * The two suggestion keys that carry a calibre-tier answer. Mirrors
 * TIER_SUGGESTION_KEYS in ReferenceEditor.tsx, which imports this.
 */
export const TIER_SUGGESTION_KEYS: ReadonlySet<string> = new Set([
  'calibreTierId',
  'calibreTierName',
]);

/**
 * Should this suggestion arrive pre-ticked in the review table, and if not, why?
 *
 * MATCHES THE BULK GRID at the settings this catalogue is curated with —
 * "high + medium" confidence, "fill empty fields only" — because the two screens
 * answer the same question about the same callable's output and disagreed:
 * the grid offered both as switches while this table hardcoded high-only and
 * ignored what the reference already held.
 *
 * That hardcoding became the bug when the enrichReference prompt changed from
 * EMPTY-FIELDS-ONLY to REPORT WHAT YOU FIND, INCLUDING AGREEMENTS. The prompt
 * now tells the model to return a field it agrees with at "high" (suggestion =
 * the stored value) and to LOWER an override to "medium" — so high-only ticked
 * the no-ops and left every genuine proposal blank. The whole table looked
 * unticked.
 *
 * The rule, in order:
 *  1. A value that could not be matched to its lookup list never pre-ticks,
 *     whatever its confidence and whatever field it is.
 *  2. Same for a `calibre` naming nothing in the catalogue. The model reports
 *     "high" on names it invented — high confidence is a claim about the WATCH
 *     carrying that movement, not evidence the movement exists here under that
 *     name — so confidence cannot stand in for the check. Its own flag rather
 *     than folded into `unverified` because the two render different notes.
 *  3. A suggestion that MATCHES what the reference already holds never pre-ticks
 *     at any confidence: there is nothing to write. It is a confirmation, and
 *     the row says so instead of asking for a decision.
 *  4. A suggestion that DIFFERS from a stored value never pre-ticks either. That
 *     is the grid's fill-empty-only rule, and it is what the prompt's "medium"
 *     is asking for — a human looks before a curated value is overwritten.
 *     EXCEPT `description` (below).
 *  5. An EMPTY field pre-ticks on high or medium. This is the case the tool
 *     exists for.
 *
 * `description` is the exception to rule 4. It is prose the prompt writes fresh on
 * every run, so it never matches the stored text and "differs" carries no signal
 * — which left every regenerated description unticked. It pre-ticks on high or
 * medium over any stored text EXCEPT a person's (`descriptionSource: 'manual'`);
 * see descriptionSource.ts. This is the single-ref table only: the bulk grid
 * keeps fill-empty, and bulk description replacement is the Description Rewrite
 * tool's job.
 *
 * The calibre tier keeps its own allowance: it pre-ticks on high or medium even
 * over a stored tier, because a tier is a pick from a closed list the request
 * itself supplied — brand-scoped, usually two or three entries — so "medium"
 * means choosing among known options rather than inventing one, and an answer
 * naming no real tier is discarded downstream by `resolveTierSuggestion` rather
 * than written. LOW still never pre-ticks. A tier that merely confirms the
 * stored one is still a no-op, so rule 3 applies to it too.
 */
export function defaultAcceptForSuggestion(
  key: string,
  confidence: string | undefined,
  unverified: boolean,
  calibreUnresolved = false,
  /**
   * How the proposal relates to the stored value. Defaults to `new` so a caller
   * that cannot compute it (no handler for the key) keeps the old behaviour for
   * an empty field rather than silently refusing to tick anything.
   */
  agreement: CellAgreement = 'new',
  /** The STORED description's provenance — only read for the `description` key. */
  storedDescriptionSource?: DescriptionSource,
  /**
   * For a BOOLEAN field: what the ref stores, with `undefined` meaning nobody
   * has answered. Pass it only for boolean keys — its presence is what selects
   * the boolean rule. The caller reads it from the row's raw `currentValue`,
   * not from the display string, because the editor renders an unset boolean
   * and a stored `false` identically.
   */
  storedBoolean?: { value: boolean | undefined },
): { accepted: boolean; reason: CheckedReason } {
  if (unverified) return { accepted: false, reason: 'unverifiedLookup' };
  if (key === 'calibre' && calibreUnresolved) {
    return { accepted: false, reason: 'unresolvedCalibre' };
  }
  if (agreement === 'matches') return { accepted: false, reason: 'matchesCurrent' };

  const c = (confidence ?? 'medium').toLowerCase();
  const meets = c === 'high' || c === 'medium';
  if (!meets) return { accepted: false, reason: 'belowThreshold' };

  // Booleans: a stored `false` is usually "nobody has said yet", so raising one
  // to `true` is the discovery this tool exists to surface — while lowering a
  // stored `true` discards an assertion and stays a judgement call. `agreement`
  // has already dealt with unset (`new`, ticked below) and with a proposal that
  // merely confirms what is stored (`matches`, returned above), so the only
  // question left here is which way a genuine disagreement runs.
  if (storedBoolean && agreement === 'differs') {
    return storedBoolean.value === false
      ? { accepted: true, reason: 'checked' }
      : { accepted: false, reason: 'existingValue' };
  }

  if (TIER_SUGGESTION_KEYS.has(key)) return { accepted: true, reason: 'checked' };
  if (agreement === 'differs' && key === 'description') {
    return descriptionRewritePreTicks(storedDescriptionSource)
      ? { accepted: true, reason: 'rewritesDescription' }
      : { accepted: false, reason: 'manualDescription' };
  }
  if (agreement === 'differs') return { accepted: false, reason: 'existingValue' };
  return { accepted: true, reason: 'checked' };
}

/** What an explicit pick in the tier picker contributes to the apply. */
export interface TierPickerContribution {
  /** True when the picker's choice should override the model's answer. */
  overridesModel: boolean;
  /** The tier id to resolve, or undefined when the choice was "no tier". */
  tierId: string | undefined;
  /** True when the choice was an explicit Clear — empty the pointer outright. */
  cleared: boolean;
}

/**
 * Fold the tier picker's state into the apply.
 *
 * `override` is null until the admin touches the picker; a string afterwards,
 * where `''` is the Clear button. An explicit choice always beats the model,
 * and because the picker only ever offers ids off the linked calibre's own
 * brand-scoped list, it needs no name resolution.
 *
 * The exception is a same-apply calibre re-link. The picker was populated from
 * the OUTGOING calibre's tiers, so its ids mean nothing on the incoming one —
 * the choice is dropped and the model's answer is left to resolve by name
 * against the new calibre. The table disables the picker in that case; this is
 * the matching guard on the write side, so a stale choice cannot survive a race
 * between the two.
 */
export function tierPickerContribution(
  override: string | null,
  calibreRelinkPending: boolean,
): TierPickerContribution {
  if (override === null || calibreRelinkPending) {
    return { overridesModel: false, tierId: undefined, cleared: false };
  }
  const id = override.trim();
  return {
    overridesModel: true,
    tierId: id || undefined,
    cleared: id === '',
  };
}

/**
 * Should the table show the "AI unsure — kept your existing tier" note?
 *
 * One-sided by design: it only fires on LOW confidence, and only when there is
 * an existing tier for it to protect. High and medium suggestions are always
 * offered (and pre-ticked — see {@link defaultAcceptForSuggestion}), so this
 * never stands between the admin and a confident answer. A ref with no tier set
 * has no human decision at stake, so a low-confidence suggestion there is shown
 * plainly, unticked, like any other low-confidence field.
 *
 * The note is advisory, never a block: the checkbox stays live so the admin can
 * apply the suggestion anyway.
 */
export function shouldHoldTierLowConfidence(
  confidence: string | undefined,
  existingTierId: string,
  override: string | null,
): boolean {
  if (override !== null) return false;
  return confidence === 'low' && !!existingTierId.trim();
}
